/**
 * WebOrderTrackingService
 * Web platform implementation of IOrderTrackingService
 * Integrates with existing OrderService and other core services
 */

import type {
  IOrderTrackingService,
  DriverLocation,
  OrderTrackingStatus,
  OrderStatusUpdate,
  RestaurantContact,
  OrderTrackingMapConfig,
} from '../../contracts/IOrderTrackingService'
import { OrderNoLongerAvailableError } from '../../contracts/OrderTrackingErrors'
import type { Order, OrderState } from '@restaurant/core'
import { OrderService, RestaurantService, getLocationForOrder, useRestaurantStore } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'


export class WebOrderTrackingService implements IOrderTrackingService {
  private orderService: OrderService

  constructor(orderService?: OrderService, _restaurantService?: RestaurantService) {
    this.orderService = orderService || new OrderService({ apiClient })
  }

  /**
   * Get current tracking status for an order
   */
  async getOrderTrackingStatus(orderId: string): Promise<OrderTrackingStatus> {
    try {
      // Get order details
      const order = await this.orderService.getOrder(orderId)

      // Get tracking history — endpoint may not exist, degrade gracefully
      let statusUpdates: OrderStatusUpdate[] = []
      try {
        const statusHistory = await this.orderService.trackOrder(orderId)
        statusUpdates = statusHistory.map(status => ({
          state: status.state,
          timestamp: status.timestamp,
          message: status.message,
          estimatedTime: status.estimatedTime,
        }))
      } catch {
        // trackOrder endpoint not available — use current order status only
      }

      const trackingStatus: OrderTrackingStatus = {
        orderId,
        currentState: order.status,
        estimatedCompletionTime: this.getEstimatedCompletionTime(order),
        actualCompletionTime: this.getActualCompletionTime(order),
        statusUpdates,
      }

      // Add pickup-specific data
      if (!order.isDelivery) {
        trackingStatus.pickupTime = order.pickupTimeEstimated ?
          this.formatTimeForDisplay(order.pickupTimeEstimated) :
          undefined
      }

      // Add delivery-specific data — store raw ISO strings so the ViewModel
      // can format them (avoids double-formatting).
      if (order.isDelivery) {
        if (order.dropoffTimeEstimated) {
          trackingStatus.deliveryTimeRange = {
            startTime: order.dropoffTimeEstimated,
            endTime: this.addMinutes(order.dropoffTimeEstimated, 10),
          }
        }

        if (order.driver?.latitude && order.driver?.longitude) {
          trackingStatus.driverLocation = {
            latitude: order.driver.latitude,
            longitude: order.driver.longitude,
            timestamp: new Date().toISOString(),
          }
        }

        if (order.driver?.name) {
          trackingStatus.driverInfo = {
            name: order.driver.name,
            phone: order.driver.dropoffPhoneNumber ?? undefined,
          }
        }
      }

      return trackingStatus
    } catch (error) {
      const isNotFound = (error as { type?: string })?.type === 'notFound'
      if (isNotFound) {
        // Order no longer exists (completed, deleted, or rotated). Not a failure —
        // callers (ViewModel) handle OrderNoLongerAvailableError by clearing tracking state.
        safeGetLogger()?.debug('Order no longer available for tracking', { domain: 'order', orderId })
        throw new OrderNoLongerAvailableError(orderId)
      }
      safeGetLogger()?.error('Failed to get order tracking status', { domain: 'order', orderId, error: String(error) })
      throw new Error('Unable to load order tracking status')
    }
  }

  /**
   * Get real-time driver location updates for delivery orders
   */
  async getDriverLocation(orderId: string): Promise<DriverLocation | null> {
    try {
      const order = await this.orderService.getOrder(orderId)

      if (!order.isDelivery || !order.driver) {
        return null
      }

      const { latitude, longitude } = order.driver
      if (!latitude || !longitude) {
        return null
      }

      return {
        latitude,
        longitude,
        timestamp: new Date().toISOString(),
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get driver location', { domain: 'order', error: String(error) })
      return null
    }
  }

  /**
   * Subscribe to real-time order status updates.
   * No-op: Supabase Realtime subscriptions are handled directly in the
   * useActiveOrderTracking hook. These stubs satisfy the IOrderTrackingService
   * interface for OrderTrackingViewModel, which calls them but receives updates
   * through the store (populated by the hook's Realtime channels).
   */
  subscribeToOrderUpdates(
    _orderId: string,
    _callback: (status: OrderTrackingStatus) => void,
    _onError?: (error: Error) => void
  ): () => void {
    return () => {}
  }

  /**
   * Subscribe to real-time driver location updates.
   * No-op: see subscribeToOrderUpdates comment above.
   */
  subscribeToDriverLocation(
    _orderId: string,
    _callback: (location: DriverLocation) => void,
    _onError?: (error: Error) => void
  ): () => void {
    return () => {}
  }

  /**
   * Get restaurant contact information
   */
  async getRestaurantContact(merchantId: string): Promise<RestaurantContact> {
    try {
      const restaurant = useRestaurantStore.getState().currentRestaurant
      if (!restaurant) {
        throw new Error('No restaurant loaded')
      }

      const location = getLocationForOrder({ merchantId }, restaurant)

      return {
        name: restaurant.name,
        phone: location?.phone || undefined,
        address: location
          ? `${location.address}, ${location.city}, ${location.state} ${location.zipCode}`
          : undefined,
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get restaurant contact', { domain: 'order', error: String(error) })
      throw new Error('Unable to load restaurant contact information')
    }
  }

  /**
   * Initiate contact with restaurant (phone call, etc.)
   */
  async contactRestaurant(merchantId: string): Promise<void> {
    try {
      const contact = await this.getRestaurantContact(merchantId)

      // For web platform, we can try to initiate a phone call
      if (contact.phone) {
        window.location.href = `tel:${contact.phone}`
      } else {
        throw new Error('Restaurant phone number not available')
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to contact restaurant', { domain: 'order', error: String(error) })
      throw new Error('Unable to contact restaurant')
    }
  }

  /**
   * Get directions to restaurant (for pickup orders)
   */
  async getDirectionsToRestaurant(merchantId: string): Promise<void> {
    try {
      const restaurant = useRestaurantStore.getState().currentRestaurant
      if (!restaurant) {
        throw new Error('No restaurant loaded')
      }

      const location = getLocationForOrder({ merchantId }, restaurant)

      if (location?.latitude && location?.longitude) {
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`
        window.open(mapsUrl, '_blank')
      } else {
        const address = `${location.address}, ${location.city}, ${location.state} ${location.zipCode}`
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
        window.open(mapsUrl, '_blank')
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get directions', { domain: 'order', error: String(error) })
      throw new Error('Directions not available')
    }
  }

  /**
   * Get map configuration for order tracking display
   */
  async getMapConfig(orderId: string, isDelivery: boolean): Promise<OrderTrackingMapConfig> {
    try {
      const order = await this.orderService.getOrder(orderId)

      const restaurant = useRestaurantStore.getState().currentRestaurant
      if (!restaurant) {
        throw new Error('No restaurant loaded')
      }

      const location = getLocationForOrder({ merchantId: order.merchantId }, restaurant)

      const config: OrderTrackingMapConfig = {
        showMap: true,
        restaurantLocation: {
          latitude: location.latitude || 0,
          longitude: location.longitude || 0,
          name: restaurant.name,
          address: `${location.address}, ${location.city}, ${location.state} ${location.zipCode}`,
        },
      }

      if (isDelivery && order.deliveryAddress) {
        config.deliveryLocation = {
          latitude: order.deliveryAddress.latitude || 0,
          longitude: order.deliveryAddress.longitude || 0,
          address: order.deliveryAddress.street,
        }

        if (order.driver?.latitude && order.driver?.longitude) {
          config.driverLocation = {
            latitude: order.driver.latitude,
            longitude: order.driver.longitude,
            timestamp: new Date().toISOString(),
          }
        }
      }

      return config
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get map config', { domain: 'order', error: String(error) })
      return { showMap: false }
    }
  }

  /**
   * Format time for display (e.g., "12:34 PM", "12:34-12:39 PM")
   */
  formatTimeForDisplay(time: string | { startTime: string; endTime: string }): string {
    if (typeof time === 'string') {
      return this.formatSingleTime(time)
    } else {
      const start = this.formatSingleTime(time.startTime)
      const end = this.formatSingleTime(time.endTime)
      return `${start}-${end}`
    }
  }

  /**
   * Calculate estimated time remaining
   */
  getTimeRemaining(estimatedTime: string): string {
    try {
      const targetTime = new Date(estimatedTime)
      const now = new Date()
      const diffMs = targetTime.getTime() - now.getTime()

      if (diffMs <= 0) {
        return 'Ready'
      }

      const diffMinutes = Math.ceil(diffMs / (1000 * 60))

      if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}`
      } else {
        const hours = Math.floor(diffMinutes / 60)
        const minutes = diffMinutes % 60
        return `${hours}h ${minutes}m`
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to calculate time remaining', { domain: 'order', error: String(error) })
      return 'Unknown'
    }
  }

  /**
   * Check if order tracking is available for the given order
   */
  isTrackingAvailable(order: Order): boolean {
    const trackableStates: OrderState[] = [
      'confirmed',
      'enrouteToPickup',
      'arrivedAtPickup',
      'pickedUp',
      'enrouteToDropoff',
      'arrivedAtDropoff',
      'delivered',
    ]

    return trackableStates.includes(order.status)
  }

  /**
   * Private helper methods
   */
  private formatSingleTime(timeString: string): string {
    try {
      const date = new Date(timeString)
      if (isNaN(date.getTime())) {
        return '--:--'
      }
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to format time', { domain: 'order', error: String(error) })
      return '--:--'
    }
  }

  private addMinutes(timeString: string, minutes: number): string {
    try {
      const date = new Date(timeString)
      date.setMinutes(date.getMinutes() + minutes)
      return date.toISOString()
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to add minutes', { domain: 'order', error: String(error) })
      return timeString
    }
  }

  private getEstimatedCompletionTime(order: Order): string | undefined {
    if (!order.isDelivery) {
      return order.pickupTimeEstimated ?? undefined
    } else {
      return order.dropoffTimeEstimated ?? undefined
    }
  }

  private getActualCompletionTime(order: Order): string | undefined {
    if (order.status === 'delivered') {
      if (order.isDelivery) {
        return order.dropOffTimeActual ?? undefined
      }
      return order.completedAt ?? undefined
    }
    return undefined
  }
}
